/* eslint-disable */
import { useState, useEffect, useReducer, useContext, useRef } from 'react';
import { toast } from 'react-toastify';
import toastError from '../../toasts/toastError';
import { useNavigate } from 'react-router-dom';

import {
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  useTheme,
} from '@mui/material';

import {
  ArrowDropDown,
  Backup,
  ContactPhone,
  Facebook,
  Instagram,
  WhatsApp,
  Search,
  DeleteOutline,
  Edit,
  CheckCircle,
  Cancel,
  Block,
} from '@mui/icons-material';

import TableRowSkeleton from '../../components/TableRowSkeleton';
import ContactModal from '../../components/ContactModal';
import ConfirmationModal from '../../components/ConfirmationModal';
import MainHeader from '../../components/MainHeader';
import Title from '../../components/Title';
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper';
import MainContainer from '../../components/MainContainer';
import { Can } from '../../components/Can';
import NewTicketModal from '../../components/NewTicketModal';
import { TagsFilter } from '../../components/TagsFilter';
import ContactImportWpModal from '../../components/ContactImportWpModal';

import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import formatSerializedId from '../../utils/formatSerializedId';
import { v4 as uuidv4 } from 'uuid';
import useCompanySettings from '../../hooks/useSettings/companySettings';
import { TicketsContext } from '../../context/Tickets/TicketsContext';
import { getStyled } from './styles';

const reducer = (state, action) => {
  if (action.type === 'LOAD_CONTACTS') {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === 'UPDATE_CONTACTS') {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === 'DELETE_CONTACT') {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const Contacts = () => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const navigate = useNavigate();

  const { user, socket } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState('');
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  const [importContactModalOpen, setImportContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [ImportContacts, setImportContacts] = useState(null);
  const whatsappToImportRef = useRef(null);

  const [blockingContact, setBlockingContact] = useState(null);
  const [unBlockingContact, setUnBlockingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [exportContact, setExportContact] = useState(false);
  const [confirmChatsOpen, setConfirmChatsOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const fileUploadRef = useRef(null);
  const [selectedTags, setSelectedTags] = useState([]);
  const { setCurrentTicket } = useContext(TicketsContext);

  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  const { getAll: getAllSettings } = useCompanySettings();
  const [hideNum, setHideNum] = useState(false);
  const [enableLGPD, setEnableLGPD] = useState(false);

  useEffect(() => {
    async function fetchData() {
      const settingList = await getAllSettings(user.companyId);

      for (const [key, value] of Object.entries(settingList)) {
        if (key === 'enableLGPD') setEnableLGPD(value === 'enabled');
        if (key === 'lgpdHideNumber') setHideNum(value === 'enabled');
      }
    }
    fetchData();
  }, [user, getAllSettings]);

  const handleMenuClick = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleImportExcel = async () => {
    try {
      const formData = new FormData();
      formData.append('file', fileUploadRef.current.files[0]);
      await api.request({
        url: `/contacts/upload`,
        method: 'POST',
        data: formData,
      });
      navigate(0);
    } catch (err) {
      toastError(err);
    }
  };

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [searchParam, selectedTags]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get('/contacts/', {
            params: {
              searchParam,
              pageNumber,
              contactTag: JSON.stringify(selectedTags),
            },
          });
          dispatch({ type: 'LOAD_CONTACTS', payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, selectedTags]);

  useEffect(() => {
    const companyId = user.companyId;

    const onContactEvent = (data) => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_CONTACTS', payload: data.contact });
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_CONTACT', payload: +data.contactId });
      }
    };
    socket.on(`company-${companyId}-contact`, onContactEvent);

    return () => {
      socket.off(`company-${companyId}-contact`, onContactEvent);
    };
  }, [socket, user]);

  const handleSelectTicket = (ticket) => {
    const code = uuidv4();
    const { id, uuid } = ticket;
    setCurrentTicket({ id, uuid, code });
  };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      handleSelectTicket(ticket);
      navigate(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSelectedTags = (selecteds) => {
    const tags = selecteds.map((t) => t.id);
    setSelectedTags(tags);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toastSuccess(i18n.t('contacts.toasts.deleted'));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam('');
    setPageNumber(1);
  };

  const handleBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: false });
      toastSuccess('Contato bloqueado');
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam('');
    setPageNumber(1);
    setBlockingContact(null);
  };

  const handleUnBlockContact = async (contactId) => {
    try {
      await api.put(`/contacts/block/${contactId}`, { active: true });
      toastSuccess('Contato desbloqueado');
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam('');
    setPageNumber(1);
    setUnBlockingContact(null);
  };

  const onSave = (whatsappId) => {
    whatsappToImportRef.current = whatsappId;
  };

  const handleimportContact = async () => {
    setImportContactModalOpen(false);

    try {
      await api.post('/contacts/import', {
        whatsappId: whatsappToImportRef.current,
      });
      navigate(0);
      setImportContactModalOpen(false);
    } catch (err) {
      toastError(err);
      setImportContactModalOpen(false);
    }
  };

  const handleimportChats = async () => {
    console.error('handleimportChats');
    try {
      await api.post('/contacts/import/chats');
      navigate(0);
    } catch (err) {
      toastError(err);
    }
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  return (
    <MainContainer sx={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby='form-dialog-title'
        contactId={selectedContactId}
      ></ContactModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t('contacts.confirmationModal.deleteTitle')} ${
                deletingContact.name
              }?`
            : blockingContact
              ? `Bloquear Contato ${blockingContact.name}?`
              : unBlockingContact
                ? `Desbloquear Contato ${unBlockingContact.name}?`
                : ImportContacts
                  ? `${i18n.t('contacts.confirmationModal.importTitlte')}`
                  : `${i18n.t('contactListItems.confirmationModal.importTitlte')}`
        }
        onSave={onSave}
        isCellPhone={ImportContacts}
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact
            ? handleDeleteContact(deletingContact.id)
            : blockingContact
              ? handleBlockContact(blockingContact.id)
              : unBlockingContact
                ? handleUnBlockContact(unBlockingContact.id)
                : ImportContacts
                  ? handleimportContact()
                  : handleImportExcel()
        }
      >
        {exportContact
          ? `${i18n.t('contacts.confirmationModal.exportContact')}`
          : deletingContact
            ? `${i18n.t('contacts.confirmationModal.deleteMessage')}`
            : blockingContact
              ? `${i18n.t('contacts.confirmationModal.blockContact')}`
              : unBlockingContact
                ? `${i18n.t('contacts.confirmationModal.unblockContact')}`
                : ImportContacts
                  ? `Escolha de qual conexão deseja importar`
                  : `${i18n.t('contactListItems.confirmationModal.importMessage')}`}
      </ConfirmationModal>
      <ConfirmationModal
        title={i18n.t('contacts.confirmationModal.importChat')}
        open={confirmChatsOpen}
        onClose={setConfirmChatsOpen}
        onConfirm={() => handleimportChats()}
      >
        {i18n.t('contacts.confirmationModal.wantImport')}
      </ConfirmationModal>
      <MainHeader>
        <Title>
          {i18n.t('contacts.title')} ({contacts.length})
        </Title>
        <MainHeaderButtonsWrapper>
          <TagsFilter onFiltered={handleSelectedTags} />
          <TextField
            sx={{ ...classes.searchField, marginBottom: theme.spacing(2) }}
            placeholder={i18n.t('contacts.searchPlaceholder')}
            type='search'
            value={searchParam}
            onChange={handleSearch}
            variant='standard'
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search />
                  </InputAdornment>
                ),
              },
            }}
          />
          <>
            <Button
              variant='outlined'
              sx={{
                ...theme.custom.editButton,
                marginLeft: theme.spacing(1),
                marginTop: theme.spacing(1),
              }}
              onClick={handleMenuClick}
            >
              Importar / Exportar
              <ArrowDropDown />
            </Button>
            <Menu
              anchorEl={menuAnchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              {...theme.custom.menuProps}
            >
              <MenuItem
                onClick={() => {
                  setConfirmOpen(true);
                  setImportContacts(true);
                  handleMenuClose();
                }}
              >
                <ContactPhone
                  fontSize='small'
                  sx={{
                    marginRight: '10px',
                  }}
                />
                {i18n.t('contacts.menu.importYourPhone')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setImportContactModalOpen(true);
                  handleMenuClose();
                }}
              >
                <Backup
                  fontSize='small'
                  sx={{
                    marginRight: '10px',
                  }}
                />
                {i18n.t('contacts.menu.importToExcel')}
              </MenuItem>
            </Menu>
          </>
          <Button
            variant='outlined'
            sx={{
              ...theme.custom.editButton,
              marginLeft: theme.spacing(1),
              marginTop: theme.spacing(1),
            }}
            onClick={handleOpenContactModal}
          >
            {i18n.t('contacts.buttons.add')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {importContactModalOpen && (
        <ContactImportWpModal
          isOpen={importContactModalOpen}
          handleClose={() => setImportContactModalOpen(false)}
          selectedTags={selectedTags}
          hideNum={hideNum}
          userProfile={user.profile}
        />
      )}
      <Paper sx={classes.mainPaper} variant='outlined' onScroll={handleScroll}>
        <>
          <input
            style={{ display: 'none' }}
            id='upload'
            name='file'
            type='file'
            accept='.xls,.xlsx'
            onChange={() => {
              setConfirmOpen(true);
            }}
            ref={fileUploadRef}
          />
        </>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell padding='checkbox' />
              <TableCell>{i18n.t('contacts.table.name')}</TableCell>
              <TableCell align='center'>
                {i18n.t('contacts.table.whatsapp')}
              </TableCell>
              <TableCell align='center'>
                {i18n.t('contacts.table.email')}
              </TableCell>
              <TableCell align='center'>{'Status'}</TableCell>
              <TableCell align='center'>
                {i18n.t('contacts.table.actions')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {contacts.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell style={{ paddingRight: 0 }}>
                    {<Avatar src={`${contact?.urlPicture}`} />}
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align='center'>
                    {enableLGPD && hideNum && user.profile === 'user'
                      ? contact.isGroup
                        ? contact.number
                        : formatSerializedId(contact?.number) === null
                          ? contact.number.slice(0, -6) +
                            '**-**' +
                            contact?.number.slice(-2)
                          : formatSerializedId(contact?.number)?.slice(0, -6) +
                            '**-**' +
                            contact?.number?.slice(-2)
                      : contact.isGroup
                        ? contact.number
                        : formatSerializedId(contact?.number)}
                  </TableCell>
                  <TableCell align='center'>{contact.email}</TableCell>
                  <TableCell align='center'>
                    {contact.active ? (
                      <CheckCircle sx={{ color: 'green' }} fontSize='small' />
                    ) : (
                      <Cancel sx={{ color: 'red' }} fontSize='small' />
                    )}
                  </TableCell>
                  <TableCell align='center'>
                    <IconButton
                      size='small'
                      disabled={!contact.active}
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                    >
                      {contact.channel === 'whatsapp' && (
                        <WhatsApp sx={{ color: 'green' }} />
                      )}
                      {contact.channel === 'instagram' && (
                        <Instagram sx={{ color: 'purple' }} />
                      )}
                      {contact.channel === 'facebook' && (
                        <Facebook sx={{ color: 'blue' }} />
                      )}
                    </IconButton>

                    <IconButton
                      size='small'
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <Edit sx={{ color: theme.custom.colors.text }} />
                    </IconButton>
                    <IconButton
                      size='small'
                      onClick={
                        contact.active
                          ? () => {
                              setConfirmOpen(true);
                              setBlockingContact(contact);
                            }
                          : () => {
                              setConfirmOpen(true);
                              setUnBlockingContact(contact);
                            }
                      }
                    >
                      {contact.active ? (
                        <Block sx={{ color: theme.custom.colors.text }} />
                      ) : (
                        <CheckCircle sx={{ color: theme.custom.colors.text }} />
                      )}
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform='contacts-page:deleteContact'
                      yes={() => (
                        <IconButton
                          size='small'
                          onClick={() => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutline
                            sx={{ color: theme.custom.colors.cancelButton }}
                          />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={6} />}
            </>
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
